import {
    Component,
    Element,
    h,
    Host,
    Prop,
    State,
} from '@stencil/core';
import { ThemeType } from '@uipath/portal-shell-util';

import store from '../../store/store';
import { getTheme } from '../../utils/themeHelper';

@Component({
    tag: 'portal-tooltip',
    styleUrl: 'portal-tooltip.scss',
})
export class PortalTooltip {
    /**
     * Tooltip html element
     *  @elementof PortalTooltip
     */
    @Element() $el: HTMLPortalTooltipElement;

    /**
     * HTML content of tooltip
     *  @memberof PortalTooltip
     */
    @Prop() $message: HTMLElement;

    /**
     * Tooltip unique ID
     *  @memberof PortalTooltip
     */
    @Prop() tooltipId = `tooltip_${tooltipIds++}`;

    /**
     * disabled: in case we want to disable the tooltip.
     *  @memberof PortalTooltip
     */
    @Prop() disabled = false;

    /**
     * position attribute. default is bottom.
     *  @memberof PortalTooltip
     */
    @Prop() position: 'top' | 'bottom' | 'left' | 'right' = 'bottom';

    /**
     * text of the tooltip to be shown.
     *  @memeberof PortalTooltip
     */
    @Prop() text: string;

    /**
     * Condition to center the tooltip
     * @memeberof PortalTooltip
     */
    @Prop() centered = false;

    /**
     * Theme override to bypass store state
     * @memberof PortalTooltip
     */
    @Prop() theme: ThemeType = 'autoTheme';

    @State() focus = false;

    private static readonly parentMaxHeight = 40;
    private static readonly parentMaxWidth = 40;
    private static readonly offset = 5;

    /**
     * componentDidRender mehtod
     *  @functionof PortalTooltip
     */
    componentDidRender() {
        if (!this.disabled) {
            this.$message.innerHTML = this.text;
        }
    }

    render() {
        return (
            <Host
                class={getTheme(store, this.theme)}
                onKeyUp={this.escapeTooltip}
                onFocus={() => this.focus = true}
                onFocusout={() => this.focus = false}>
                <div
                    class={`portal-tooltip ${this.focus ? 'focus' : ''}`}
                    onMouseOver={e => this.showTooltip(e)}
                    onFocus={e => this.showTooltip(e)}
                    onMouseLeave={() => this.focus = false}
                >
                    <slot></slot>
                    {!this.disabled && <span id={this.tooltipId} class={`message ${this.position}`} role="tooltip" ref={el => {
                        if (el) {
                            this.$message = el;
                        }
                    }}></span>}
                </div>
            </Host>
        );
    }

    private escapeTooltip(e: KeyboardEvent) {
        if (e.key === 'Escape') {
            if (this.focus) {
                e.preventDefault();
                e.stopPropagation();
            }
            this.focus = false;
            (e.target as HTMLElement).blur();
        }
    }

    private showTooltip(e: Event) {
        if (this.focus) {
            return;
        }
        const targetElem = e.target as HTMLElement;
        if (!targetElem) {
            return;
        }
        const tooltipElem = this.getToolTipElem(targetElem);
        if (!tooltipElem) {
            return;
        }
        const tooltipElemBoundingRect = tooltipElem.getBoundingClientRect();
        const targetElemBoundingRect = targetElem.getBoundingClientRect();
        tooltipElem.style.top = this.getTopOffset(targetElemBoundingRect, tooltipElemBoundingRect);
        tooltipElem.style.left = this.getLeftOffSet(targetElemBoundingRect, tooltipElemBoundingRect);
        this.focus = true;
    }

    private getLeftOffSet(targetElemBoundingRect: DOMRect, tooltipElemBoundingRect: DOMRect) {
        const targetElemWidth = targetElemBoundingRect.width < PortalTooltip.parentMaxWidth
            ? targetElemBoundingRect.width
            : PortalTooltip.parentMaxWidth;
        if (this.centered) {
            return `${targetElemBoundingRect.left + targetElemBoundingRect.width / 2 - tooltipElemBoundingRect.width / 2}px`;
        }
        // If tooltip goes beyond the viewport, open to the left of the element
        if (targetElemBoundingRect.right + tooltipElemBoundingRect.width + PortalTooltip.offset > window.innerWidth) {
            return `${targetElemBoundingRect.left - tooltipElemBoundingRect.width - PortalTooltip.offset}px`;
        }
        return `${targetElemBoundingRect.left + targetElemWidth + PortalTooltip.offset}px`;
    }

    private getTopOffset(targetElemBoundingRect: DOMRect, tooltipElemBoundingRect: DOMRect) {
        const targetElemHeight = targetElemBoundingRect.height < PortalTooltip.parentMaxHeight
            ? targetElemBoundingRect.height
            : PortalTooltip.parentMaxHeight;
        // If tooltip goes beyond the viewport, open it above the element
        if (targetElemBoundingRect.bottom + tooltipElemBoundingRect.height + PortalTooltip.offset > window.innerHeight) {
            return `${targetElemBoundingRect.top - tooltipElemBoundingRect.height - PortalTooltip.offset}px`;
        }
        return `${targetElemBoundingRect.top + targetElemHeight + PortalTooltip.offset}px`;
    }

    private getToolTipElem(element: HTMLElement): HTMLElement | null {
        if (element.classList.contains('portal-tooltip')) {
            return element.querySelector('span.message');
        }
        // Hovered element is other than portal-tooltip.
        // It may be possible that inside portal-tooltip slot we passed other html(Example is material icon)
        return element.closest('portal-tooltip')?.querySelector('span.message') as HTMLElement;
    }
}

// tracking global tooltip ids.
// todo, pradeep : Move it to Store
let tooltipIds = 0;
